import { translate } from "@/i18n";
import { desktopScreenRecordingMessages } from "@/i18n/messages/desktop-screen-recording";
import { invoke } from "@tauri-apps/api/core";
import { create } from "zustand";
import { uploadImage, type UploadedImage } from "@/api/uploads";
import {
  nativeAttachmentFile,
  type NativeAttachmentFile,
} from "@/lib/native-clipboard-files";
import { subscribeDesktopChatAuthScope } from "./desktop-chat-auth-scope";

type RecordingMedia = {
  url?: string;
  loading?: boolean;
  uploading?: boolean;
  progress?: number;
  error?: string;
};
export const useScreenRecordingMedia = create<{
  clips: Record<string, RecordingMedia>;
}>(() => ({ clips: {} }));
const files = new Map<string, Promise<File>>();
const uploads = new Map<string, Promise<UploadedImage | null>>();
let generation = 0;
const versions = new Map<string, number>();
type Owner = { generation: number; version: number };
const ownerFor = (id: string): Owner => ({
  generation,
  version: versions.get(id) ?? 0,
});
const isCurrent = (id: string, owner: Owner) =>
  owner.generation === generation && owner.version === (versions.get(id) ?? 0);

function update(id: string, value: Partial<RecordingMedia>, owner: Owner) {
  if (!isCurrent(id, owner)) return;
  useScreenRecordingMedia.setState((state) => ({
    clips: { ...state.clips, [id]: { ...state.clips[id], ...value } },
  }));
}

export function clearScreenRecordingMedia(): void {
  generation += 1;
  for (const media of Object.values(useScreenRecordingMedia.getState().clips))
    if (media.url) URL.revokeObjectURL(media.url);
  files.clear();
  uploads.clear();
  versions.clear();
  useScreenRecordingMedia.setState({ clips: {} });
}
subscribeDesktopChatAuthScope(clearScreenRecordingMedia);

export function releaseScreenRecordingMedia(clipId: string): void {
  versions.set(clipId, (versions.get(clipId) ?? 0) + 1);
  const clips = { ...useScreenRecordingMedia.getState().clips };
  if (clips[clipId]?.url) URL.revokeObjectURL(clips[clipId]!.url!);
  delete clips[clipId];
  files.delete(clipId);
  uploads.delete(clipId);
  useScreenRecordingMedia.setState({ clips });
}

/** Only an exact native-owned recording ID can read an original. */
export function loadScreenRecordingMedia(clipId: string): Promise<File> {
  const cached = files.get(clipId);
  if (cached) return cached;
  const owner = ownerFor(clipId);
  update(clipId, { loading: true, error: undefined }, owner);
  const file = invoke<NativeAttachmentFile>("read_saved_screen_recording", {
    clipId,
  })
    .then((data) => {
      if (!isCurrent(clipId, owner))
        throw new Error("The recording session changed.");
      const file = nativeAttachmentFile(data);
      update(clipId, { url: URL.createObjectURL(file), loading: false }, owner);
      return file;
    })
    .catch((error) => {
      if (isCurrent(clipId, owner)) files.delete(clipId);
      update(clipId, { loading: false, error: String(error) }, owner);
      throw error;
    });
  files.set(clipId, file);
  return file;
}

/** Saving and local playback are independent from the optional remote copy. */
export function uploadScreenRecordingMedia(
  clipId: string,
): Promise<UploadedImage | null> {
  const cached = uploads.get(clipId);
  if (cached) return cached;
  const owner = ownerFor(clipId);
  update(
    clipId,
    { uploading: true, progress: undefined, error: undefined },
    owner,
  );
  const uploaded = loadScreenRecordingMedia(clipId)
    .then((file) => {
      if (!isCurrent(clipId, owner))
        throw new Error("The recording session changed.");
      return uploadImage(file, (progress) =>
        update(clipId, { progress }, owner),
      );
    })
    .then((image) => {
      update(clipId, { uploading: false, progress: undefined }, owner);
      return image;
    })
    .catch((error) => {
      if (isCurrent(clipId, owner)) uploads.delete(clipId);
      update(
        clipId,
        {
          uploading: false,
          error: translate(
            desktopScreenRecordingMessages,
            "uploadUnavailable",
            {
              error: String(error),
            },
          ),
        },
        owner,
      );
      return null;
    });
  uploads.set(clipId, uploaded);
  return uploaded;
}
